import { Users, Copy, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { PageContainer } from '@/components/Container'
import { useState } from 'react'
import { ManageFriendsModal } from '@/components/friends/ManageFriendsModal'
import { FriendsLeaderboard } from '@/components/friends/FriendsLeaderboard'
import { 
  useFriends, 
  usePendingRequests,
  useSentRequests, 
  useInviteCode,
  useAcceptFriendRequest,
  useRejectFriendRequest,
  useRefreshFriendsData 
} from '@/hooks/useFriends'

export default function Friends() {
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [isCopied, setIsCopied] = useState(false)
  const [isManageFriendsOpen, setIsManageFriendsOpen] = useState(false)
  
  // Use real API hooks
  const { data: friends = [], isLoading: friendsLoading } = useFriends()
  const { data: pendingRequests = [], isLoading: requestsLoading } = usePendingRequests()
  const { data: sentRequests = [], isLoading: sentRequestsLoading } = useSentRequests()
  const { data: inviteData } = useInviteCode()
  
  const acceptRequest = useAcceptFriendRequest()
  const rejectRequest = useRejectFriendRequest()
  const refreshData = useRefreshFriendsData()
  
  const inviteUrl = inviteData?.url || ''
  const isLoading = friendsLoading || requestsLoading || sentRequestsLoading

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl)
      setIsCopied(true)
      setTimeout(() => setIsCopied(false), 2000)
    } catch {
      // Fallback for browsers that don't support clipboard API
      const input = document.createElement('input')
      input.value = inviteUrl
      document.body.appendChild(input)
      input.select()
      document.execCommand('copy')
      document.body.removeChild(input)
      setIsCopied(true)
      setTimeout(() => setIsCopied(false), 2000)
    }
  }

  const handleRefresh = async () => {
    setIsRefreshing(true)
    await refreshData()
    setIsRefreshing(false)
  }

  const handleManageFriends = () => {
    setIsManageFriendsOpen(true)
  }

  const handleAcceptRequest = async (requesterId: string) => {
    acceptRequest.mutate(requesterId)
  }

  const handleDeclineRequest = async (requesterId: string, userName: string) => {
    if (confirm(`Are you sure you want to decline the friend request from ${userName}?`)) {
      rejectRequest.mutate(requesterId)
    }
  }

  return (
    <PageContainer>
      <div className="space-y-6">
        {/* Page Header Section */}
        <section className="bg-white dark:bg-gray-800 rounded-2xl p-8 max-sm:p-4 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="font-bebas-neue text-5xl tracking-[2px] uppercase text-foreground mb-2">
                Friends
              </h1>
              <p className="font-oswald text-lg font-normal text-muted-foreground">
                Compare scores and compete with your friends
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                size="default"
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="gap-2"
              >
                <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button
                variant="outline"
                size="default"
                onClick={handleManageFriends}
                className="gap-2 font-oswald uppercase tracking-[1px] font-semibold"
              >
                <Users className="h-4 w-4" />
                Manage friends
              </Button>
            </div>
          </div>
        </section>

        {/* Friends Leaderboard Section */}
        <FriendsLeaderboard />

        {/* Add Friends Section */}
        <section className="bg-white dark:bg-gray-800 rounded-2xl p-8 max-sm:p-4 shadow-sm">
          <h2 className="font-bebas-neue text-4xl tracking-[2px] uppercase mb-8">
            Add Friends
          </h2>
          
          {/* Share Invite Link */}
          <div className="mb-12">
            <h3 className="font-oswald text-lg uppercase tracking-[1px] font-semibold mb-4">
              Share Invite Link
            </h3>
            <p className="text-muted-foreground mb-6">
              Share this link to invite friends. Anyone who clicks it can send you a friend request.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 max-w-2xl">
              <input
                type="text"
                value={inviteUrl}
                readOnly
                className="flex-1 min-w-0 px-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm font-mono truncate"
              />
              <Button
                variant="outline"
                size="default"
                onClick={handleCopyLink}
                className="gap-2 flex-shrink-0"
              >
                <Copy className="h-4 w-4" />
                {isCopied ? 'COPIED!' : 'COPY LINK'}
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-3 italic">
              Send via email, text message, or post to social media
            </p>
          </div>

          {/* Pending Requests */}
          <div>
            <h3 className="font-oswald text-lg uppercase tracking-[1px] font-semibold mb-6">
              Pending Requests
            </h3>
            {isLoading ? (
              <p className="text-muted-foreground text-center py-12">
                Loading...
              </p>
            ) : pendingRequests.length === 0 ? (
              <p className="text-muted-foreground text-center py-12">
                No pending friend requests
              </p>
            ) : (
              <div className="space-y-3">
                {pendingRequests.map((request) => {
                  const userName = request.user.firstName && request.user.lastName 
                    ? `${request.user.firstName} ${request.user.lastName}`
                    : request.user.email
                  const initials = request.user.firstName && request.user.lastName
                    ? `${request.user.firstName[0]}${request.user.lastName[0]}`.toUpperCase()
                    : request.user.email.slice(0, 2).toUpperCase()
                  
                  return (
                    <div key={request.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className="h-12 w-12 flex-shrink-0 rounded-full bg-gray-600 dark:bg-gray-500 text-white flex items-center justify-center font-semibold">
                          {initials}
                        </div>
                        <div className="min-w-0">
                          <p className="font-oswald font-semibold text-lg truncate">{userName}</p>
                          <p className="text-sm text-muted-foreground">
                            Sent you a friend request
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        <Button
                          size="sm"
                          variant="default"
                          onClick={() => handleAcceptRequest(request.userId)}
                          disabled={acceptRequest.isPending}
                        >
                          Accept
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeclineRequest(request.userId, userName)}
                          disabled={rejectRequest.isPending}
                        >
                          Decline
                        </Button>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </section>
        
        {/* Sent Friend Requests Section */}
        <section className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-sm:p-4 shadow-sm">
          <h2 className="font-oswald text-xl font-semibold mb-6">Sent Friend Requests</h2>
          <div className="min-h-[120px] relative">
            {isLoading ? (
              <p className="text-muted-foreground text-center py-12">
                Loading...
              </p>
            ) : sentRequests.length === 0 ? (
              <p className="text-muted-foreground text-center py-12">
                No sent friend requests
              </p>
            ) : (
              <div className="space-y-3">
                {sentRequests.map((request) => {
                  const userName = request.friend.firstName && request.friend.lastName 
                    ? `${request.friend.firstName} ${request.friend.lastName}`
                    : request.friend.email
                  const initials = request.friend.firstName && request.friend.lastName
                    ? `${request.friend.firstName[0]}${request.friend.lastName[0]}`.toUpperCase()
                    : request.friend.email.slice(0, 2).toUpperCase()
                  
                  return (
                    <div key={request.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className="h-12 w-12 flex-shrink-0 rounded-full bg-gray-600 dark:bg-gray-500 text-white flex items-center justify-center font-semibold">
                          {initials}
                        </div>
                        <div className="min-w-0">
                          <p className="font-oswald font-semibold text-lg truncate">{userName}</p>
                          <p className="text-sm text-muted-foreground">
                            Friend request sent
                          </p>
                        </div>
                      </div>
                      <span className="text-sm text-muted-foreground flex-shrink-0">
                        Waiting for response
                      </span>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </section>
      </div>
      
      {/* Manage Friends Modal */}
      <ManageFriendsModal
        isOpen={isManageFriendsOpen}
        onClose={() => setIsManageFriendsOpen(false)}
        inviteUrl={inviteUrl}
        friends={friends}
      />
    </PageContainer>
  )
}